import { ORDContragent } from '@synapex/prisma/client'
import {
    ContragentResponse,
    CreateVkOrdContragentInputDto,
    GetContragentsInputDto,
    UpdateVkOrdContragentInputDto,
} from '@synapex/shared2/gateway-rest-api'
import {
    useMutation,
    useQuery,
    useQueryClient,
    UseQueryOptions,
} from '@tanstack/react-query'
import { api } from '../base'
import { apiQueryKey } from '../query-key'
import { pickDto } from '../utils'
import { createContragent, getContragents } from './ord.service'

export function useGetContragents(
    input: GetContragentsInputDto,
    options?: Omit<UseQueryOptions<ContragentResponse[], Error>, 'queryKey'>
) {
    return useQuery<ContragentResponse[], Error>({
        queryKey: apiQueryKey.contragents(input, input.roles),
        queryFn: () => getContragents(input),

        staleTime: 1000 * 60 * 5,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        ...options,
    })
}

export function useCreateContragent() {
    const queryClient = useQueryClient()

    return useMutation<ORDContragent, Error, CreateVkOrdContragentInputDto>({
        mutationFn: (input) => createContragent(input),
        onSuccess(data, variables) {
            void queryClient.invalidateQueries({
                queryKey: apiQueryKey.contragents(variables, variables.roles),
            })

            void queryClient.invalidateQueries({
                queryKey: apiQueryKey.checkInn(variables),
            })

            // if advertiser or agency, then invalidate additionalOrdData
            if (
                data.roles.includes('advertiser') ||
                data.roles.includes('agency')
            ) {
                void queryClient.invalidateQueries({
                    queryKey: apiQueryKey.additionalOrdData(variables),
                })
            } else {
                void queryClient.invalidateQueries({
                    queryKey: apiQueryKey.markingFormData({
                        // eslint-disable-next-line @typescript-eslint/no-misused-spread
                        ...variables,
                    }),
                })
            }
        },
    })
}

export function useUpdateContragent2() {
    const queryClient = useQueryClient()

    return useMutation<
        ORDContragent,
        Error,
        {
            contragentId: number
            input: UpdateVkOrdContragentInputDto
        }
    >({
        mutationFn: ({ contragentId, input }) =>
            api.put<ORDContragent>(
                `/ord/contragents/v2/${contragentId}`,
                pickDto(UpdateVkOrdContragentInputDto, input)
            ),
        onSuccess(_, { input }) {
            void queryClient.invalidateQueries({
                queryKey: apiQueryKey.contragents(input, input.roles || []),
            })

            void queryClient.invalidateQueries({
                queryKey: apiQueryKey.checkInn(input, input.inn),
            })
        },
    })
}
